import { useState, useEffect, useCallback } from 'react';
import firebaseDataService from '../../../../services/FirebaseDataService';
import { 
  InventoryItem, 
  InventoryCategory, 
  Warehouse, 
  QuickSaleScreenData, 
  ScreenSettings,
  Customer
} from '../QuickSaleScreen';
import { loadCashDrawerPrinter } from '../utils/cashDrawerStorage';
import { getSellerByScreen, type SellerSnapshot } from '../../../../services/sellerInfoService';
import { getPersonnel as getPersonnelSql, getScreenPermissionsByType } from '../../../../services/migration/personnelDataService';
import { getActiveTerminalContext } from '../../../../services/auth/terminalSession';
import { DEFAULT_PAVO_CARD_MEDIATOR } from '../../../../services/pavoMediatorConfig';
// localStorage için anahtarlar
const INVENTORY_ITEMS_KEY = 'quickSaleInventoryItems';
const INVENTORY_ITEMS_TIMESTAMP_KEY = 'quickSaleInventoryItemsTimestamp';
const WAREHOUSES_KEY = 'quickSaleWarehouses';
const WAREHOUSES_TIMESTAMP_KEY = 'quickSaleWarehousesTimestamp';
const CATEGORIES_KEY = 'quickSaleCategories';
const CATEGORIES_TIMESTAMP_KEY = 'quickSaleCategoriesTimestamp';

// Önbellek geçerlilik süresi (5 dakika)
const CACHE_VALIDITY_MS = 5 * 60 * 1000;

const GENERAL_CUSTOMER_ID = 'GENERAL_CUSTOMER_ACCOUNT';
const GENERAL_CUSTOMER_NAME = 'Genel Cari';

// Personel interface'i
interface Personnel {
  id: string;
  name: string;
  email?: string;
}

// User data interface
interface UserData {
  firstName: string;
  lastName: string;
  role?: string;
  email?: string;
  uid?: string;
  branchesId?: string | null;
}

// PYK Screen interface
interface PykScreenData {
  id: string;
  name: string;
  branchId?: string | null;
  branchName?: string;
}

// Payment Option interface
interface PaymentOption {
  id: string;
  type: 'cash' | 'pos' | 'openAccount' | 'voucher' | 'receipt';
  label: string;
  enabled: boolean;
  cashboxId?: string | null;
  bankAccountId?: string | null;
  subAccountId?: string | null;
  defaultAccountId?: string | null;
  timestamp?: number;
}

// ✅ YENİ: QuickSaleScreenData için extended interface (source property ile)
interface QuickSaleScreenDataWithSource extends QuickSaleScreenData {
  source?: 'firebase' | 'sql';
}

interface UseQuickSaleDataProps {
  // own-auth: yalnızca uid/email kullanılıyor — firebase/auth `User` yerine minimal tip.
  currentUser: { uid: string; email: string | null } | null;
  routeScreenId: string | undefined;
  navigate: (path: string, options?: { replace?: boolean }) => void;
  refreshingSettings?: boolean;
  selectedCustomer: Customer | null;
  setSelectedCustomer: (customer: Customer | null) => void;
}

interface UseQuickSaleDataReturn {
  // Company & User Data
  companyId: string | null;
  quickSaleUserData: UserData | null;
  
  // Screens
  currentScreen: QuickSaleScreenData | null;
  permittedScreens: QuickSaleScreenData[];
  permittedPykScreens: PykScreenData[];
  allScreens: Record<string, QuickSaleScreenData>;
  
  // Screen Settings
  screenSettings: ScreenSettings | null;
  prepaidSystemEnabled: boolean;
  showProductImages: boolean;

  // ✅ PR2 (Invoice Seller Snapshot Refactor): Satıcı bilgisi
  // Backend SellerResolver tarafından çözülür (Companies vs Branches.Firm*).
  // Fatura kaydında DTO'da gönderilse de backend tekrar resolve eder; bu state
  // yalnızca UI gösterim ve fiş başlığı (PrintReceiptService) için kullanılır.
  seller: SellerSnapshot | null;
  loadingSeller: boolean;
  sellerError: string | null;
  
  // Inventory Data
  allInventoryItems: InventoryItem[];
  allCategoriesData: InventoryCategory[];
  allWarehouses: Warehouse[];
  
  // Personnel
  allPersonnel: Personnel[];
  
  // Payment Options
  cashPaymentLabel: string;
  cashPaymentCashboxId: string | null;
  cashPaymentCashboxName: string | null; // ✅ YENİ: Kasa adı (gün sonu raporu için)
  posOptions: PaymentOption[];
  singlePosOption: PaymentOption | null;
  openAccountPaymentOption: PaymentOption | null;
  
  // Loading States
  loadingScreens: boolean;
  loadingScreenSettings: boolean;
  loadingInventory: boolean;
  loadingWarehouses: boolean;
  loadingCategories: boolean;
  
  // Error States
  screenError: string | null;
  screenSettingsError: string | null;
  inventoryError: string | null;
  
  // Actions
  refreshScreenSettings: () => Promise<void>;
  setCompanyId: (id: string | null) => void;
  setCurrentScreen: (screen: QuickSaleScreenData | null) => void;
  setScreenSettings: (settings: ScreenSettings | null) => void;
}

export const useQuickSaleData = ({
  currentUser,
  routeScreenId,
  navigate,
  refreshingSettings = false,
  selectedCustomer,
  setSelectedCustomer,
}: UseQuickSaleDataProps): UseQuickSaleDataReturn => {
  
  // ========== STATE DECLARATIONS ==========
  
  // Company & User
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [quickSaleUserData, setQuickSaleUserData] = useState<UserData | null>(null);
  
  // Screens
  const [currentScreen, setCurrentScreen] = useState<QuickSaleScreenData | null>(null);
  const [permittedScreens, setPermittedScreens] = useState<QuickSaleScreenData[]>([]);
  const [permittedPykScreens, setPermittedPykScreens] = useState<PykScreenData[]>([]);
  const [allScreens, setAllScreens] = useState<Record<string, QuickSaleScreenData>>({});
  
  // Screen Settings
  const [screenSettings, setScreenSettings] = useState<ScreenSettings | null>(null);
  const [prepaidSystemEnabled, setPrepaidSystemEnabled] = useState<boolean>(false);
  const [showProductImages, setShowProductImages] = useState<boolean>(true);
  // refreshScreenSettings tetikleyicisi: artırıldığında ekran ayarları useEffect'i SQL'den yeniden yükler.
  const [settingsRefreshNonce, setSettingsRefreshNonce] = useState<number>(0);

  // ✅ PR2: Satıcı snapshot (Companies vs Branches.Firm*)
  const [seller, setSeller] = useState<SellerSnapshot | null>(null);
  const [loadingSeller, setLoadingSeller] = useState<boolean>(false);
  const [sellerError, setSellerError] = useState<string | null>(null);
  
  // Inventory Data
  const [allInventoryItems, setAllInventoryItems] = useState<InventoryItem[]>([]);
  const [allCategoriesData, setAllCategoriesData] = useState<InventoryCategory[]>([]);
  const [allWarehouses, setAllWarehouses] = useState<Warehouse[]>([]);
  
  // Personnel
  const [allPersonnel, setAllPersonnel] = useState<Personnel[]>([]);
  
  // Payment Options
  const [cashPaymentLabel, setCashPaymentLabel] = useState<string>('NAKİT');
  const [cashPaymentCashboxId, setCashPaymentCashboxId] = useState<string | null>(null);
  const [cashPaymentCashboxName, setCashPaymentCashboxName] = useState<string | null>(null); // ✅ YENİ: Kasa adı (gün sonu raporu için)
  const [posOptions, setPosOptions] = useState<PaymentOption[]>([]);
  const [singlePosOption, setSinglePosOption] = useState<PaymentOption | null>(null);
  const [openAccountPaymentOption, setOpenAccountPaymentOption] = useState<PaymentOption | null>(null);
  
  // Loading States
  const [loadingScreens, setLoadingScreens] = useState<boolean>(true);
  const [loadingScreenSettings, setLoadingScreenSettings] = useState<boolean>(true);
  const [loadingInventory, setLoadingInventory] = useState<boolean>(true);
  const [loadingWarehouses, setLoadingWarehouses] = useState<boolean>(true);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(true);
  
  // Error States
  const [screenError, setScreenError] = useState<string | null>(null);
  const [screenSettingsError, setScreenSettingsError] = useState<string | null>(null);
  const [inventoryError, setInventoryError] = useState<string | null>(null);

  // ========== useEffect 1: Şirket ID ve Kullanıcı Bilgilerini Çek ==========
  useEffect(() => {
    const fetchInitialData = async () => {
      if (currentUser?.uid) {
        console.log(`%c[useQuickSaleData] fetchInitialData currentUser UID: ${currentUser.uid}`, "color: orange;");
        try {
          const userData = await firebaseDataService.getUserData(currentUser.uid);

          if (userData) {
            const role = userData.role || 'employee';
            setCompanyId(userData.companyId);
            
            setQuickSaleUserData({
              firstName: userData.firstName || '',
              lastName: userData.lastName || '',
              role: role,
              email: currentUser.email || undefined,
              uid: currentUser.uid,
              branchesId: userData.branchesId || null
            });
            
            console.log("[useQuickSaleData] Kullanıcı Rolü:", role, "Şirket ID:", userData.companyId);
            console.log("[useQuickSaleData] 🏢 Şube ID (branchesId):", userData.branchesId, "| Tip:", typeof userData.branchesId);
          }
        } catch (error) {
          console.error("[useQuickSaleData] İlk veri yükleme hatası:", error);
        }
      }
    };
    
    fetchInitialData();
  }, [currentUser]);

  // ========== useEffect 2: Yetkili ve Tüm Ekranları Çek ==========
  useEffect(() => {
    if (!companyId || !currentUser?.uid) {
      setLoadingScreens(true);
      return;
    }

    setLoadingScreens(true);
    setScreenError(null);

    const fetchScreensAndPermissions = async () => {
      try {
        const currentUserRole = quickSaleUserData?.role;

        // ✅ 1. SQL'DEN TÜM EKRANLARI ÇEK
        const { default: salesService } = await import('../../../../services/salesService');
        const sqlScreens = await salesService.getQuickSaleScreens();
        
        // Sadece aktif ekranları filtrele
        const activeScreens = sqlScreens.filter(screen => screen.IsActive !== false);
        
        const fetchedAllScreens: Record<string, QuickSaleScreenData> = {};
        
        if (activeScreens.length > 0) {
          activeScreens.forEach(screen => {
            fetchedAllScreens[screen.Id.toString()] = { 
              id: screen.Id.toString(), 
              name: screen.Name,
              branchId: screen.BranchId || null // ✅ YENİ: Ekranın şube ID'si
            };
          });
          setAllScreens(fetchedAllScreens);
          console.log(`✅ [useQuickSaleData] ${activeScreens.length} aktif ekran SQL'den yüklendi`);
        } else {
          setAllScreens({});
          console.warn("[useQuickSaleData] Hiç hızlı satış ekranı bulunamadı.");
        }

        let permittedScreenIds: string[] = [];
        let currentPersonnelId: string | null = null;

        // Satış terminali oturumu: ekran, personel girişiyle değil 11 haneli kodla
        // açıldı. Terminal kullanıcısının HR personel kaydı yoktur, dolayısıyla
        // aşağıdaki personel eşleştirme dalı "personel kaydı bulunamadı" ile düşer
        // ve ekran hiç açılmaz. Bu yüzden dal atlanıp izinli ekran kümesi cihazın
        // bağlı olduğu tek ekrana sabitleniyor.
        //
        // Alternatif olarak terminal kullanıcısına `admin` rolü verilebilirdi (üstteki
        // dal her ekranı açardı) ama o token terminal makinesinde duruyor ve admin
        // tüm yönetim/muhasebe uçlarına erişim demek. Bu yol hem rolü `employee`
        // tutuyor hem terminale yalnızca kendi ekranını gösteriyor.
        //
        // Ekran İÇİ yetkiler (iade, iskonto, gün sonu…) buradan gelmiyor; terminal
        // oturumunda kasiyer PIN girişinden geliyor (useCashierAuth).
        //
        // linkedScreenId her modda QuickSaleScreens.Id DEĞİL: hızlı satışta o ekranın
        // kendi Id'si, restoranda RestaurantScreens.Id, pakette PackageScreens.Id.
        // Bu yüzden restoran/paket için bağlı QuickSaleScreens kaydına çevriliyor;
        // çeviri tutmazsa (eski kayıtlarda ters bağ boş olabilir) rota/prop ile gelen
        // ekran Id'si kullanılıyor — restoran ekranı onu RestaurantScreens.QuickSaleScreenId
        // üzerinden verdiği için terminalin kapsamı dışına çıkmıyor.
        const terminalContext = getActiveTerminalContext();
        const terminalLinkedId =
          terminalContext?.linkedScreenId != null ? String(terminalContext.linkedScreenId) : null;
        const routeScreenIdIfKnown =
          routeScreenId && fetchedAllScreens[routeScreenId] ? routeScreenId : null;

        let terminalScreenId: string | null = null;
        if (terminalLinkedId) {
          if (terminalContext?.deviceMode === 'sales_quicksale') {
            terminalScreenId = terminalLinkedId;
          } else if (terminalContext?.deviceMode === 'sales_restaurant') {
            terminalScreenId =
              activeScreens
                .find(s => s.RestaurantScreenId != null && String(s.RestaurantScreenId) === terminalLinkedId)
                ?.Id?.toString() ?? routeScreenIdIfKnown;
          } else if (terminalContext?.deviceMode === 'sales_package') {
            terminalScreenId =
              activeScreens
                .find(s => s.PackageScreenId != null && String(s.PackageScreenId) === terminalLinkedId)
                ?.Id?.toString() ?? routeScreenIdIfKnown;
          }
        }

        // 2. Rol Bazlı Yetki Kontrolü
        if (terminalScreenId) {
          permittedScreenIds = fetchedAllScreens[terminalScreenId] ? [terminalScreenId] : [];
          if (permittedScreenIds.length === 0) {
            console.error(
              `[useQuickSaleData] Terminal koduna bağlı ekran bulunamadı veya aktif değil: ${terminalScreenId}`
            );
          } else {
            console.log('[useQuickSaleData] Terminal oturumu — izinli ekran:', terminalScreenId);
          }
        } else if (currentUserRole === 'admin') {
          permittedScreenIds = Object.keys(fetchedAllScreens);
        } else {
          // Faz 3: personel + yetki SQL'den. Kanonik kimlik = uid; email fallback korunur.
          const fetchedAllPersonnel: Personnel[] = [];
          try {
            const sqlPersonnel = await getPersonnelSql(companyId);
            sqlPersonnel.forEach((p) => {
              fetchedAllPersonnel.push({
                id: p.id, // uid
                name: p.name || 'İsimsiz Personel',
                email: (p.email as string) || undefined,
              });
            });
          } catch (e) {
            console.error('[useQuickSaleData] SQL personel okuma hatasi:', e);
          }
          setAllPersonnel(fetchedAllPersonnel);

          // 1) Once kanonik uid ile esle, 2) yoksa email fallback, 3) yine de uid'i kullan.
          if (currentUser?.uid && fetchedAllPersonnel.some(p => p.id === currentUser.uid)) {
            currentPersonnelId = currentUser.uid;
          } else if (currentUser?.email) {
            const matchedPersonnel = fetchedAllPersonnel.find(
              p => p.email?.toLowerCase() === currentUser.email?.toLowerCase()
            );
            if (matchedPersonnel) {
              currentPersonnelId = matchedPersonnel.id;
              console.log(`[useQuickSaleData] E-posta ile eşleşen personel ID bulundu: ${currentPersonnelId}`);
            }
          }
          if (!currentPersonnelId && currentUser?.uid) {
            currentPersonnelId = currentUser.uid;
          }

          if (!currentPersonnelId && currentUserRole !== 'admin') {
            console.error(`[useQuickSaleData] Kullanıcı için eşleşen personel kaydı bulunamadı.`);
            setScreenError("Kullanıcıya ait personel kaydı bulunamadı veya yetkiler alınamadı.");
            setLoadingScreens(false);
            setCurrentScreen(null);
            return;
          }

          // Personelin yetkilerini SQL'den çek (quickSaleScreens screenType).
          try {
            const perms = await getScreenPermissionsByType(currentPersonnelId!, 'quickSaleScreens');
            permittedScreenIds = perms
              .filter(r => r.isEnabled && r.screenId)
              .map(r => String(r.screenId));
            console.log("[useQuickSaleData] SQL yetkili ekran ID'leri:", permittedScreenIds);
          } catch (e) {
            console.warn('[useQuickSaleData] SQL yetki okuma hatasi:', e);
          }

          if (permittedScreenIds.length === 0) {
            console.warn(`[useQuickSaleData] Personel için yetki kaydı bulunamadı (SQL).`);
          }
        }

        // 3. Yetkili Ekran Listesini Oluştur
        const userPermittedScreens = permittedScreenIds
          .map(id => fetchedAllScreens[id])
          .filter(screen => !!screen)
          .sort((a, b) => a.name.localeCompare(b.name));
        
        setPermittedScreens(userPermittedScreens);

        // 4. Mevcut Ekranı Ayarla
        if (routeScreenId && fetchedAllScreens[routeScreenId] && permittedScreenIds.includes(routeScreenId)) {
          setCurrentScreen(fetchedAllScreens[routeScreenId]);
        } else if (userPermittedScreens.length > 0) {
          const defaultScreen = userPermittedScreens[0];
          setCurrentScreen(defaultScreen);
          console.log("[useQuickSaleData] Varsayılan yetkili ekran ayarlandı:", defaultScreen);
          
          if (!routeScreenId || (routeScreenId && !permittedScreenIds.includes(routeScreenId))) {
            console.warn(`[useQuickSaleData] URL'deki screenId geçersiz. Varsayılan ekrana yönlendiriliyor.`);
            navigate(`/hizli-satis/ekran/${defaultScreen.id}`, { replace: true });
          }
        } else {
          setCurrentScreen(null);
          // Terminal oturumunda "bu kullanıcı için yetkili ekran yok" mesajı yanıltıcı:
          // sorun yetki değil, kodun bağlandığı ekranın silinmiş/pasif olması.
          setScreenError(
            terminalScreenId
              ? 'Bu terminal koduna bağlı hızlı satış ekranı bulunamadı. Ekran silinmiş veya pasif olabilir.'
              : 'Bu kullanıcı için yetkili hızlı satış ekranı bulunamadı.'
          );
          console.error("[useQuickSaleData] Yetkili hızlı satış ekranı bulunamadı.");
        }
        
        // 5. Yetkili PYK Ekranlarını Çek
        await fetchPermittedPykScreens(currentUserRole === 'admin', currentPersonnelId);

      } catch (error) {
        console.error("[useQuickSaleData] Hızlı satış ekranları ve yetkiler yüklenirken hata:", error);
        setScreenError("Ekran bilgileri ve yetkiler yüklenirken bir hata oluştu.");
        setCurrentScreen(null);
      } finally {
        setLoadingScreens(false);
      }
    };
    
    // Yetkili PYK ekranlarını çeken fonksiyon (SQL).
    // PYK ekran tanımları: salesService.getPykScreens() (GET /api/PykScreen).
    // Personel ataması: PykScreen.SettingsJson.assignedPersonnel (RTDB permissions/pykScreens KULLANILMIYOR — bkz. Login.tsx).
    const fetchPermittedPykScreens = async (isAdmin: boolean, personnelId: string | null) => {
      try {
        const { default: salesService } = await import('../../../../services/salesService');
        const sqlPykScreens = await salesService.getPykScreens();

        if (!sqlPykScreens || sqlPykScreens.length === 0) {
          console.log("[useQuickSaleData] Hiç PYK ekranı bulunamadı (SQL).");
          setPermittedPykScreens([]);
          return;
        }

        let pykScreensList: PykScreenData[] = [];

        for (const screen of sqlPykScreens) {
          let allowed = isAdmin;

          if (!allowed && personnelId && screen.SettingsJson) {
            try {
              const settings = JSON.parse(screen.SettingsJson);
              const assigned: any[] = settings?.assignedPersonnel || [];
              allowed = Array.isArray(assigned) && assigned.map(String).includes(String(personnelId));
            } catch {
              allowed = false;
            }
          }

          if (allowed) {
            pykScreensList.push({
              id: String(screen.Id),
              name: screen.Name || 'İsimsiz PYK Ekranı',
              branchId: screen.BranchId || null
            });
          }
        }

        pykScreensList.sort((a, b) => a.name.localeCompare(b.name));
        setPermittedPykScreens(pykScreensList);

      } catch (error) {
        console.error("[useQuickSaleData] PYK ekranları yüklenirken hata:", error);
      }
    };

    fetchScreensAndPermissions();

  }, [companyId, currentUser?.uid, routeScreenId, navigate, quickSaleUserData?.role, currentUser?.email]);

  // ========== useEffect 3: Mevcut Ekran Ayarlarını Çek (SQL veya Firebase Hybrid) ==========
  useEffect(() => {
    if (!currentScreen || !currentScreen.id || !companyId) {
      if (!loadingScreenSettings) {
        setScreenSettings(null);
        setPrepaidSystemEnabled(false);
      }
      return;
    }

    const loadScreenSettings = async () => {
      const startTime = performance.now();
      console.log(`⏱️ [useQuickSaleData] Ekran ayarları yükleme başladı: ${new Date().toISOString()} (Screen ID: ${currentScreen.id})`);
      
      setLoadingScreenSettings(true);
      setScreenSettingsError(null);

      try {
        // ✅ SADECE SQL - Artık tüm ekranlar SQL'de
        console.log(`⏱️ [useQuickSaleData] SQL ekranı ayarları çekiliyor... ID: ${currentScreen.id} (${performance.now() - startTime}ms)`);
        
        const { default: salesService } = await import('../../../../services/salesService');
        const screenIdAsNumber = parseInt(currentScreen.id, 10);
        
        if (isNaN(screenIdAsNumber)) {
          throw new Error(`Geçersiz SQL ekran ID'si: ${currentScreen.id}`);
        }

        const apiStartTime = performance.now();
        const screenData = await salesService.getQuickSaleScreenById(screenIdAsNumber);
        console.log(`⏱️ [useQuickSaleData] API çağrısı tamamlandı: ${performance.now() - apiStartTime}ms`);
        
        // SQL'den gelen JSON'ları parse et
        const fetchedSettings: ScreenSettings = {};
        
        // Payment Options
        if (screenData.PaymentOptionsJson) {
          try {
            const paymentOptionsArray = JSON.parse(screenData.PaymentOptionsJson);
            console.log('📋 [PAYMENT OPTIONS] Raw JSON:', screenData.PaymentOptionsJson);
            console.log('📋 [PAYMENT OPTIONS] Parsed array:', paymentOptionsArray);
            const paymentOptionsObj: { [id: string]: any } = {};
            paymentOptionsArray.forEach((opt: any) => {
              const { id, ...rest } = opt;
              paymentOptionsObj[id] = rest;
            });
            fetchedSettings.paymentOptions = paymentOptionsObj;
            console.log('📋 [PAYMENT OPTIONS] Final object:', paymentOptionsObj);
            
            // Debug: other ve online ödeme sayısını logla
            const otherCount = paymentOptionsArray.filter((opt: any) => opt.type === 'other' && opt.enabled).length;
            const onlineCount = paymentOptionsArray.filter((opt: any) => opt.type === 'online' && opt.enabled).length;
            console.log('📋 [PAYMENT OPTIONS] Other count:', otherCount, 'Online count:', onlineCount);
          } catch (e) {
            console.error("PaymentOptionsJson parse hatası:", e);
          }
        } else {
          console.log('📋 [PAYMENT OPTIONS] PaymentOptionsJson BOŞ veya YOK');
        }

        // Stock Visibility
        if (screenData.StockVisibilityJson) {
          try {
            fetchedSettings.stockVisibility = JSON.parse(screenData.StockVisibilityJson);
          } catch (e) {
            console.error("StockVisibilityJson parse hatası:", e);
          }
        }

        // Printing Settings
        if (screenData.PrintingSettingsJson) {
          try {
            const printingSettings = JSON.parse(screenData.PrintingSettingsJson);
            // ✅ Raw JSON'ı da ekle (PrintReceiptService için)
            // @ts-ignore
            fetchedSettings.PrintingSettingsJson = screenData.PrintingSettingsJson;
            // @ts-ignore - Bu özellikler ScreenSettings'de tanımlı olmayabilir ama runtime'da kullanılıyor
            fetchedSettings.definedPrinters = printingSettings.definedPrinters || [];
            // @ts-ignore
            fetchedSettings.categoryPrinterAssignments = printingSettings.categoryPrinterAssignments || {};
            // ✅ YENİ: Kategori başına 2. yazıcı (opsiyonel)
            // @ts-ignore
            fetchedSettings.categoryPrinterAssignments2 = printingSettings.categoryPrinterAssignments2 || {};
            // @ts-ignore
            fetchedSettings.printingMode = printingSettings.printingMode || 'category';
            // @ts-ignore
            fetchedSettings.defaultReceiptPrinter = printingSettings.defaultReceiptPrinter || null;
            // @ts-ignore
            fetchedSettings.receiptDisplaySettings = printingSettings.receiptDisplaySettings || undefined;
            // ✅ YENİ: Adisyon yazıcısı (kategori bazlı modda restoran için)
            // @ts-ignore
            fetchedSettings.adisyonPrinter = printingSettings.adisyonPrinter || null;
            // ✅ YENİ: Kategorisiz ürün yazıcısı (kategori bazlı mod)
            // @ts-ignore
            fetchedSettings.uncategorizedPrinter = printingSettings.uncategorizedPrinter || null;
            // ✅ YENİ: Yazarkasa + Fiş Yazıcısı birlikte kullanım
            // @ts-ignore
            fetchedSettings.useBothFiscalAndReceiptPrinter = printingSettings.useBothFiscalAndReceiptPrinter || false;
            // 💰 Not: cashDrawerPrinter bilgisayara özel olduğu için SQL'den
            // OKUNMAZ. Aşağıdaki "Para çekmecesi tetik yazıcısı" bloğunda
            // sadece localStorage'dan yüklenir.
          } catch (e) {
            console.error("PrintingSettingsJson parse hatası:", e);
          }
        }

        // Assigned Personnel
        if (screenData.AssignedPersonnelJson) {
          try {
            fetchedSettings.assignedPersonnel = JSON.parse(screenData.AssignedPersonnelJson);
          } catch (e) {
            console.error("AssignedPersonnelJson parse hatası:", e);
          }
        }

        // Personnel Permissions
        if (screenData.PersonnelPermissionsJson) {
          try {
            fetchedSettings.personnelPermissions = JSON.parse(screenData.PersonnelPermissionsJson);
          } catch (e) {
            console.error("PersonnelPermissionsJson parse hatası:", e);
          }
        }

        // Boolean ayarlar
        fetchedSettings.prepaidSystemEnabled = screenData.PrepaidSystemEnabled || false;
        fetchedSettings.showProductImages = screenData.ShowProductImages !== false;
        fetchedSettings.useFiscalDeviceForCash = screenData.UseFiscalDeviceForCash || false;
        // @ts-ignore - ✅ YENİ: Yazarkasa bağlantı uyarısı (varsayılan açık)
        fetchedSettings.warnIfFiscalNotConnected = screenData.WarnIfFiscalNotConnected !== undefined ? screenData.WarnIfFiscalNotConnected : true;
        fetchedSettings.usePavoPayForOtherPayments = screenData.UsePavoPayForOtherPayments || false;
        fetchedSettings.useExternalPrinterForPavo = screenData.UseExternalPrinterForPavo || false;
        // Kart bacağının gideceği Pavo aracısı. Kolon eklenmeden önceki ekranlar
        // 0/undefined döndürebiliyor, geçersiz değerde BKM/TechPOS'a düşülür.
        // Burada okunmadığı sürece QuickSaleScreen her yüklemede varsayılanı
        // global store'a yazıp AlbarakaPOS (38) seçimini eziyordu.
        const cardMediator = Number((screenData as any).PavoPaymentMediatorId);
        // @ts-ignore
        fetchedSettings.pavoPaymentMediatorId = Number.isFinite(cardMediator) && cardMediator > 0
          ? cardMediator
          : DEFAULT_PAVO_CARD_MEDIATOR;
        // ✅ YENİ: Rest-P2P modu
        fetchedSettings.useRestP2PForPayments = (screenData as any).UseRestP2PForPayments === true;
        fetchedSettings.pavoRestP2PDeviceScreenCode = (screenData as any).PavoRestP2PDeviceScreenCode || null;
        fetchedSettings.allowAccountCreationOnSale = screenData.AllowAccountCreationOnSale || false;
        fetchedSettings.showCashChangeModal = screenData.ShowCashChangeModal || false;
        // @ts-ignore - ✅ YENİ: Seçili yazarkasa markası
        fetchedSettings.selectedFiscalDevice = screenData.SelectedFiscalDevice || 'hugin_t300';
        // @ts-ignore
        fetchedSettings.useKitchenScreen = screenData.UseKitchenScreen || false;
        // @ts-ignore
        fetchedSettings.showScreenLayoutButton = screenData.ShowScreenLayoutButton !== false;
        // @ts-ignore
        fetchedSettings.selectedQRMenuId = screenData.SelectedQRMenuId || null;
        // @ts-ignore - ✅ YENİ: Kasiyer Sistemi Kullanımı
        fetchedSettings.useCashierSystem = screenData.UseCashierSystem || false;
        // @ts-ignore - ✅ YENİ: Yerel Servis URL
        fetchedSettings.localServiceUrl = screenData.LocalServiceUrl || 'http://localhost:3003';
        console.log('🔌 [useQuickSaleData] LocalServiceUrl SQL\'den:', screenData.LocalServiceUrl, '→ Kullanılan:', fetchedSettings.localServiceUrl);
        // Tartı ayarları. Ayar modalı kaydettikten sonra parent'a camelCase verdiği
        // için ilk kayıtta çalışıyor gibi görünüyor; burada okunmadığı sürece
        // sayfa yenilenince canlı tartım ve ağırlık barkodu sessizce kapanıyor.
        // @ts-ignore
        fetchedSettings.useScale = (screenData as any).UseScale === true;
        // @ts-ignore
        fetchedSettings.selectedScaleId = (screenData as any).SelectedScaleId ?? null;
        // @ts-ignore
        fetchedSettings.scaleReadMode = (screenData as any).ScaleReadMode || 'live';
        // @ts-ignore
        fetchedSettings.readWeightBarcode = (screenData as any).ReadWeightBarcode === true;
        fetchedSettings.autoLockEnabled = screenData.AutoLockEnabled || false;
        fetchedSettings.autoLockTimeoutSeconds = screenData.AutoLockTimeoutSeconds || 30;
        try {
          fetchedSettings.predefinedDeleteReasons = screenData.PredefinedDeleteReasonsJson
            ? JSON.parse(screenData.PredefinedDeleteReasonsJson)
            : [];
        } catch { fetchedSettings.predefinedDeleteReasons = []; }
        try {
          fetchedSettings.predefinedItemNotes = screenData.PredefinedItemNotesJson
            ? JSON.parse(screenData.PredefinedItemNotesJson)
            : [];
        } catch { fetchedSettings.predefinedItemNotes = []; }
        
        // ✅ localStorage'dan ek ayarları oku ve SQL değerleriyle merge et
        // SQL boolean değerleri (useCashierSystem, useKitchenScreen vb.) her zaman öncelikli
        const settingsKey = `quickSaleSettings_${companyId}_${currentScreen.id}`;
        let finalSettings = fetchedSettings;
        try {
          const localStorageJson = localStorage.getItem(settingsKey);
          if (localStorageJson) {
            const localStorageSettings = JSON.parse(localStorageJson);

            // localStorage'dan sadece SQL'de olmayan ayarları al (eski uyumluluk için)
            // SQL boolean değerlerini ASLA localStorage'dan alma!
            finalSettings = {
              ...localStorageSettings, // localStorage'dan gelen ek ayarlar (eski format)
              ...fetchedSettings,      // ✅ SQL değerleri her zaman öncelikli!
            };
          }
        } catch (e) {
          console.error("[useQuickSaleData] localStorage okuma hatası:", e);
        }

        // ✅ Local yazıcı ayarları override: Bu bilgisayara özel yazıcı ayarları varsa uygula
        try {
          const localPrinterKey = `localPrinterSettings_${companyId}_${currentScreen.id}`;
          const localPrinterJson = localStorage.getItem(localPrinterKey);
          if (localPrinterJson) {
            const localPrinterSettings = JSON.parse(localPrinterJson);
            if (localPrinterSettings.enabled) {
              console.log('🖨️ [useQuickSaleData] Local yazıcı ayarları aktif, override uygulanıyor');
              // @ts-ignore
              finalSettings.printingMode = localPrinterSettings.printingMode || finalSettings.printingMode;
              // @ts-ignore
              finalSettings.defaultReceiptPrinter = localPrinterSettings.defaultReceiptPrinter || finalSettings.defaultReceiptPrinter;
              // @ts-ignore
              finalSettings.categoryPrinterAssignments = localPrinterSettings.categoryPrinterAssignments || finalSettings.categoryPrinterAssignments;
              // @ts-ignore
              finalSettings.categoryPrinterAssignments2 = localPrinterSettings.categoryPrinterAssignments2 || finalSettings.categoryPrinterAssignments2;
              // @ts-ignore
              finalSettings.adisyonPrinter = localPrinterSettings.adisyonPrinter || finalSettings.adisyonPrinter;
              // @ts-ignore
              finalSettings.uncategorizedPrinter = localPrinterSettings.uncategorizedPrinter || finalSettings.uncategorizedPrinter;
              // @ts-ignore
              finalSettings.definedPrinters = localPrinterSettings.definedPrinters || finalSettings.definedPrinters;
              // @ts-ignore
              finalSettings.useBothFiscalAndReceiptPrinter = localPrinterSettings.useBothFiscalAndReceiptPrinter ?? finalSettings.useBothFiscalAndReceiptPrinter;

              // PrintingSettingsJson'ı da güncelle (PrintReceiptService bunu kullanıyor)
              const updatedPrintingJson = JSON.stringify({
                printingMode: localPrinterSettings.printingMode,
                defaultReceiptPrinter: localPrinterSettings.defaultReceiptPrinter,
                categoryPrinterAssignments: localPrinterSettings.categoryPrinterAssignments,
                categoryPrinterAssignments2: localPrinterSettings.categoryPrinterAssignments2,
                adisyonPrinter: localPrinterSettings.adisyonPrinter,
                uncategorizedPrinter: localPrinterSettings.uncategorizedPrinter,
                definedPrinters: localPrinterSettings.definedPrinters,
                useBothFiscalAndReceiptPrinter: localPrinterSettings.useBothFiscalAndReceiptPrinter,
                receiptDisplaySettings: (finalSettings as any).receiptDisplaySettings,
              });
              // @ts-ignore
              finalSettings.PrintingSettingsJson = updatedPrintingJson;
            }
          }
        } catch (e) {
          console.error("[useQuickSaleData] Local yazıcı ayarları okuma hatası:", e);
        }

        // 💰 Para çekmecesi tetik yazıcısı → SADECE localStorage'dan oku
        // (Bilgisayara özel ayar; SQL'e yazılmaz, SQL'den okunmaz.)
        try {
          const cashDrawerPrinter = loadCashDrawerPrinter(companyId, currentScreen.id);
          // @ts-ignore
          finalSettings.cashDrawerPrinter = cashDrawerPrinter;
          console.log(
            '💰 [useQuickSaleData] cashDrawerPrinter (localStorage):',
            cashDrawerPrinter?.name || '(YOK)'
          );
        } catch (e) {
          console.warn('[useQuickSaleData] cashDrawerPrinter localStorage okuma hatası:', e);
          // @ts-ignore
          finalSettings.cashDrawerPrinter = null;
        }

        setScreenSettings(finalSettings);
        setPrepaidSystemEnabled(fetchedSettings.prepaidSystemEnabled || false);
        setShowProductImages(fetchedSettings.showProductImages !== false);
        
        console.log(`⏱️ [useQuickSaleData] ✅ Ekran ayarları SQL'den yüklendi (Toplam süre: ${performance.now() - startTime}ms)`);
        console.log("📦 [useQuickSaleData] StockVisibilityJson:", fetchedSettings.stockVisibility);

        // ✅ Ödeme Seçeneklerini İşle
        if (fetchedSettings.paymentOptions) {
          const enabledOptions: any[] = [];
          const activePosOptions: any[] = [];
          let foundCashOption: any = null;
          let foundOpenAccountOption: any = null;

          const paymentOptionsArray = Object.entries(fetchedSettings.paymentOptions);
          for (const [id, optionData] of paymentOptionsArray) {
            if ((optionData as any).enabled) {
              const currentOption = { id, ...optionData };
              enabledOptions.push(currentOption);
              
              if ((optionData as any).type === 'cash' && !foundCashOption) {
                foundCashOption = currentOption;
              } else if ((optionData as any).type === 'pos') {
                activePosOptions.push(currentOption);
              } else if ((optionData as any).type === 'openAccount' && !foundOpenAccountOption) {
                foundOpenAccountOption = currentOption;
              }
            }
          }

          // ✅ DÜZELTME: Kasa ve Banka adlarını SQL'den çek (eski kayıtlarda cashboxName/bankAccountName olmayabilir)
          let cashboxesMap: { [id: string]: string } = {};
          let bankAccountsMap: { [id: string]: string } = {};
          
          try {
            const { default: accountingService } = await import('../../../../services/accountingService');
            
            // Kasaları çek
            const cashboxes = await accountingService.getCashboxes();
            if (Array.isArray(cashboxes)) {
              cashboxes.forEach((cb: any) => {
                const cbId = (cb.id || cb.Id)?.toString();
                const cbName = cb.name || cb.Name || '';
                if (cbId && cbName) {
                  cashboxesMap[cbId] = cbName;
                }
              });
            }
            console.log(`✅ [useQuickSaleData] ${Object.keys(cashboxesMap).length} kasa yüklendi (ad eşleme için)`);
            
            // Banka hesaplarını çek
            const bankAccounts = await accountingService.getBankAccounts();
            if (Array.isArray(bankAccounts)) {
              bankAccounts.forEach((ba: any) => {
                const baId = (ba.id || ba.Id)?.toString();
                const baName = ba.bankName || ba.BankName || '';
                if (baId && baName) {
                  bankAccountsMap[baId] = baName;
                }
                // Alt hesapları da ekle
                const subAccounts = ba.subAccounts || ba.SubAccounts;
                if (subAccounts && Array.isArray(subAccounts)) {
                  subAccounts.forEach((sub: any) => {
                    const subId = (sub.id || sub.Id)?.toString();
                    const subName = sub.accountName || sub.AccountName || '';
                    if (subId && subName) {
                      bankAccountsMap[subId] = subName;
                    }
                  });
                }
              });
            }
            console.log(`✅ [useQuickSaleData] ${Object.keys(bankAccountsMap).length} banka hesabı yüklendi (ad eşleme için)`);
          } catch (err) {
            console.error('❌ [useQuickSaleData] Kasa/Banka adları yüklenirken hata:', err);
          }

          // Nakit State'lerini Ayarla
          if (foundCashOption) {
            setCashPaymentLabel(foundCashOption.label || 'NAKİT');
            setCashPaymentCashboxId(foundCashOption.cashboxId || null);
            // ✅ DÜZELTME: Önce cashboxName'e bak, yoksa cashboxId ile gerçek kasa adını bul
            let realCashboxName = foundCashOption.cashboxName;
            if (!realCashboxName && foundCashOption.cashboxId) {
              realCashboxName = cashboxesMap[foundCashOption.cashboxId] || null;
            }
            setCashPaymentCashboxName(realCashboxName);
            console.log(`💰 [useQuickSaleData] Nakit kasa: ID=${foundCashOption.cashboxId}, Gerçek Ad=${realCashboxName}`);
          } else {
            setCashPaymentLabel('NAKİT');
            setCashPaymentCashboxId(null);
            setCashPaymentCashboxName(null);
          }

          // ✅ DÜZELTME: POS seçeneklerinde bankAccountName yoksa gerçek banka adını bul
          const enrichedPosOptions = activePosOptions.map(posOpt => {
            let realBankName = posOpt.bankAccountName;
            if (!realBankName && posOpt.bankAccountId) {
              realBankName = bankAccountsMap[posOpt.bankAccountId] || null;
            }
            // Alt hesap adı için de kontrol
            let realSubAccountName = posOpt.subAccountName;
            if (!realSubAccountName && posOpt.subAccountId) {
              realSubAccountName = bankAccountsMap[posOpt.subAccountId] || null;
            }
            return {
              ...posOpt,
              bankAccountName: realBankName,
              subAccountName: realSubAccountName
            };
          });

          // POS State'lerini Ayarla
          setPosOptions(enrichedPosOptions);
          if (enrichedPosOptions.length === 1) {
            setSinglePosOption(enrichedPosOptions[0]);
          } else {
            setSinglePosOption(null);
          }

          // Açık Hesap State'ini Ayarla
          if (foundOpenAccountOption) {
            setOpenAccountPaymentOption(foundOpenAccountOption);
          } else {
            setOpenAccountPaymentOption(null);
          }
        }

      } catch (error) {
        console.error(`❌ [useQuickSaleData] Ekran ayarları yüklenirken hata:`, error);
        setScreenSettingsError("Ekran ayarları yüklenirken bir hata oluştu.");
        setPrepaidSystemEnabled(false);
      } finally {
        setLoadingScreenSettings(false);
      }
    };

    loadScreenSettings();
  }, [currentScreen?.id, companyId, settingsRefreshNonce]); // ✅ currentScreen yerine currentScreen?.id kullan (stable reference); nonce ile manuel yenileme

  // ========== useEffect 4: Tüm Envanter Ürünlerini Çek ==========
  // ❌ ARTIK KULLANILMIYOR - useQuickSaleProducts hook'u SQL'den ürünleri çekiyor
  // Firebase'den toplu ürün çekme işlemi kaldırıldı (5000+ ürün gereksiz yere yükleniyordu)
  useEffect(() => {
    if (companyId) {
      // Eski önbellek verilerini temizle
      try {
        localStorage.removeItem(`${INVENTORY_ITEMS_KEY}_${companyId}`);
        localStorage.removeItem(`${INVENTORY_ITEMS_TIMESTAMP_KEY}_${companyId}`);
      } catch (error) {
        console.error("[useQuickSaleData] localStorage temizlenirken hata:", error);
      }
      
      // allInventoryItems artık kullanılmıyor, boş bırak
      setAllInventoryItems([]);
      setLoadingInventory(false);
      console.log('[useQuickSaleData] ✅ Ürün yüklemesi artık useQuickSaleProducts hook\'u tarafından SQL\'den yapılıyor');
    }
  }, [companyId]);

  // ========== useEffect 5: Tüm Depoları Çek (SQL) ==========
  useEffect(() => {
    if (!companyId) return;

    const loadWarehouses = async () => {
      // Önce localStorage'dan kontrol et
      let cachedWarehouses: Warehouse[] | null = null;
      let cachedTimestamp: number | null = null;
      
      try {
        const cachedWarehousesJson = localStorage.getItem(`${WAREHOUSES_KEY}_${companyId}`);
        const cachedTimestampStr = localStorage.getItem(`${WAREHOUSES_TIMESTAMP_KEY}_${companyId}`);
        
        if (cachedWarehousesJson && cachedTimestampStr) {
          cachedWarehouses = JSON.parse(cachedWarehousesJson);
          cachedTimestamp = parseInt(cachedTimestampStr, 10);
        }
      } catch (error) {
        console.error("[useQuickSaleData] LocalStorage'dan depo verisi okunurken hata:", error);
        cachedWarehouses = null;
        cachedTimestamp = null;
      }
      
      const now = Date.now();
      const isCacheValid = cachedWarehouses && 
                           cachedTimestamp && 
                           now - cachedTimestamp < CACHE_VALIDITY_MS;
      
      if (isCacheValid && cachedWarehouses) {
        console.log(`[useQuickSaleData] Depolar önbellekten yüklendi. Depo sayısı: ${cachedWarehouses.length}`);
        setAllWarehouses(cachedWarehouses);
        setLoadingWarehouses(false);
        return;
      }

      // ✅ SQL'den depoları çek
      setLoadingWarehouses(true);
      console.log('[useQuickSaleData] 🔄 Depolar SQL\'den yükleniyor...');
      
      try {
        const { default: accountingService } = await import('../../../../services/accountingService');
        const sqlWarehouses = await accountingService.getWarehouses(false); // includeItemCount = false
        
        const warehouseList: Warehouse[] = sqlWarehouses.map((wh: any) => ({
          id: wh.id?.toString() || wh.Id?.toString() || '',
          name: wh.name || wh.Name || 'İsimsiz Depo'
        }));
        
        setAllWarehouses(warehouseList);
        
        // localStorage'a kaydet
        try {
          localStorage.setItem(`${WAREHOUSES_KEY}_${companyId}`, JSON.stringify(warehouseList));
          localStorage.setItem(`${WAREHOUSES_TIMESTAMP_KEY}_${companyId}`, now.toString());
          console.log(`✅ [useQuickSaleData] ${warehouseList.length} depo SQL'den yüklendi`);
        } catch (storageError) {
          console.error("[useQuickSaleData] Depolar localStorage'a kaydedilirken hata:", storageError);
        }
      } catch (error) {
        console.error("❌ [useQuickSaleData] Depolar SQL'den çekilirken hata:", error);
        
        // Hata durumunda önbellekteki verileri kullan
        if (cachedWarehouses) {
          console.log("[useQuickSaleData] SQL hatası nedeniyle önbellekteki depolar kullanılıyor");
          setAllWarehouses(cachedWarehouses);
        } else {
          setAllWarehouses([]);
        }
      } finally {
        setLoadingWarehouses(false);
      }
    };

    loadWarehouses();
  }, [companyId]);

  // ========== useEffect 6: Tüm Kategorileri Çek (SQL) ==========
  useEffect(() => {
    if (!companyId) return;

    const loadCategories = async () => {
      // Önce localStorage'dan kontrol et
      let cachedCategories: InventoryCategory[] | null = null;
      let cachedTimestamp: number | null = null;
      
      try {
        const cachedCategoriesJson = localStorage.getItem(`${CATEGORIES_KEY}_${companyId}`);
        const cachedTimestampStr = localStorage.getItem(`${CATEGORIES_TIMESTAMP_KEY}_${companyId}`);
        
        if (cachedCategoriesJson && cachedTimestampStr) {
          cachedCategories = JSON.parse(cachedCategoriesJson);
          cachedTimestamp = parseInt(cachedTimestampStr, 10);
        }
      } catch (error) {
        console.error("[useQuickSaleData] LocalStorage'dan kategori verisi okunurken hata:", error);
        cachedCategories = null;
        cachedTimestamp = null;
      }
      
      const now = Date.now();
      const isCacheValid = cachedCategories && 
                           cachedTimestamp && 
                           now - cachedTimestamp < CACHE_VALIDITY_MS;
      
      if (isCacheValid && cachedCategories) {
        console.log(`[useQuickSaleData] Kategoriler önbellekten yüklendi. Kategori sayısı: ${cachedCategories.length}`);
        setAllCategoriesData(cachedCategories);
        setLoadingCategories(false);
        return;
      }

      // ✅ SQL'den kategorileri çek
      setLoadingCategories(true);
      console.log('[useQuickSaleData] 🔄 Kategoriler SQL\'den yükleniyor...');
      
      try {
        const { default: accountingService } = await import('../../../../services/accountingService');
        const sqlCategories = await accountingService.getCategories(false); // includeItemCount = false
        
        const categoryList: InventoryCategory[] = sqlCategories.map((cat: any) => ({
          id: cat.id?.toString() || cat.Id?.toString() || '',
          name: cat.name || cat.Name || 'İsimsiz Kategori',
          parentId: cat.parentId || cat.ParentId || null,
          level: typeof cat.level === 'number' ? cat.level : (typeof cat.Level === 'number' ? cat.Level : 1)
        }));
        
        setAllCategoriesData(categoryList);
        
        // localStorage'a kaydet
        try {
          localStorage.setItem(`${CATEGORIES_KEY}_${companyId}`, JSON.stringify(categoryList));
          localStorage.setItem(`${CATEGORIES_TIMESTAMP_KEY}_${companyId}`, now.toString());
          console.log(`✅ [useQuickSaleData] ${categoryList.length} kategori SQL'den yüklendi`);
        } catch (storageError) {
          console.error("[useQuickSaleData] Kategoriler localStorage'a kaydedilirken hata:", storageError);
        }
      } catch (error) {
        console.error("❌ [useQuickSaleData] Kategoriler SQL'den çekilirken hata:", error);
        
        // Hata durumunda önbellekteki verileri kullan
        if (cachedCategories) {
          console.log("[useQuickSaleData] SQL hatası nedeniyle önbellekteki kategoriler kullanılıyor");
          setAllCategoriesData(cachedCategories);
        } else {
          setAllCategoriesData([]);
        }
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, [companyId]);

  // ========== ACTIONS ==========
  
  /**
   * Refresh screen settings from Firebase
   * Memoized to prevent infinite loops
   */
  const refreshScreenSettings = useCallback(async () => {
    if (!currentScreen || !companyId) {
      console.warn("[useQuickSaleData] refreshScreenSettings: currentScreen veya companyId yok");
      return;
    }
    // Ayarlar artık SQL'de. RTDB okuması yerine ana SQL yükleyici useEffect'ini yeniden tetikle
    // (tüm parse + kasa/banka ad eşleme + localStorage merge mantığı tek yerde kalır).
    console.log("[useQuickSaleData] Ekran ayarları SQL'den yeniden yükleniyor (nonce).");
    setSettingsRefreshNonce((n) => n + 1);
  }, [currentScreen?.id, companyId]); // currentScreen.id'yi izle, tüm currentScreen'i değil

  // ========== useEffect: Satıcı (Seller) Snapshot Çek ==========
  // PR2 (Invoice Seller Snapshot Refactor):
  // currentScreen.id geldiğinde backend'den satıcı bilgisini çek. Backend Companies veya
  // Branches.Firm* arasında karar verir; UI burada sadece tüketir. Fatura kaydı sırasında
  // backend tekrar resolve eder, bu yüzden frontend'in göndermesi şart değil — sadece
  // gösterim ve fiş başlığı için tutuyoruz.
  useEffect(() => {
    if (!currentScreen?.id) {
      setSeller(null);
      return;
    }
    const screenIdNum = Number(currentScreen.id);
    if (!Number.isFinite(screenIdNum) || screenIdNum <= 0) {
      console.warn('[useQuickSaleData] Geçersiz screenId — seller fetch atlandı:', currentScreen.id);
      setSeller(null);
      return;
    }

    let cancelled = false;
    setLoadingSeller(true);
    setSellerError(null);

    getSellerByScreen(screenIdNum, 'QuickSale')
      .then((snapshot) => {
        if (cancelled) return;
        setSeller(snapshot);
        console.log(
          '[useQuickSaleData] 🏪 Seller snapshot:',
          `${snapshot.sellerCompanyName} (${snapshot.source}, franchise=${snapshot.isFranchiseInvoice})`,
        );
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[useQuickSaleData] Seller fetch hatası:', err);
        setSellerError(err?.message || 'Satıcı bilgisi alınamadı');
        setSeller(null);
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingSeller(false);
      });

    return () => {
      cancelled = true;
    };
  }, [currentScreen?.id]);

  return {
    // Company & User Data
    companyId,
    quickSaleUserData,
    
    // Screens
    currentScreen,
    permittedScreens,
    permittedPykScreens,
    allScreens,
    
    // Screen Settings
    screenSettings,
    prepaidSystemEnabled,
    showProductImages,

    // ✅ PR2: Satıcı snapshot
    seller,
    loadingSeller,
    sellerError,
    
    // Inventory Data
    allInventoryItems,
    allCategoriesData,
    allWarehouses,
    
    // Personnel
    allPersonnel,
    
    // Payment Options
    cashPaymentLabel,
    cashPaymentCashboxId,
    cashPaymentCashboxName, // ✅ YENİ: Kasa adı (gün sonu raporu için)
    posOptions,
    singlePosOption,
    openAccountPaymentOption,
    
    // Loading States
    loadingScreens,
    loadingScreenSettings,
    loadingInventory,
    loadingWarehouses,
    loadingCategories,
    
    // Error States
    screenError,
    screenSettingsError,
    inventoryError,
    
    // Actions
    refreshScreenSettings,
    setCompanyId,
    setCurrentScreen,
    setScreenSettings,
  };
};

